export type Player = {
  id: string;
  displayName: string | null;
  ageConfirmed: boolean;
  geoAllowed: boolean;
  geoCountry: string | null;
  welcomeBonusGranted: boolean;
  isBlocked: boolean;
  blockReason: string | null;
  pointsBalance: number;
  onboardingComplete: boolean;
};

export type PublicRound = {
  id: string;
  sequence: number;
  status: string;
  bankPoints: number;
  startsAt: string;
  endsAt: string;
  actualEndedAt: string | null;
  finalizedAt: string | null;
  nextStartsAt: string | null;
  remaining: Record<string, number>;
  rewards: Record<string, number>;
  nextBankPreview: number;
  participantsCount: number;
};

export type PublicClue = {
  roundedDistanceMeters: number | null;
  directionDegrees: number | null;
  directionShown: boolean;
  claimResult: string;
  createdAt: string;
};

export type PublicFeedEvent = {
  id: string;
  playerId: string | null;
  message: string;
  createdAt: string;
  metadata: Record<string, unknown>;
};

export type PublicLeaderboardRow = {
  playerId: string;
  displayName: string;
  pendingRewards: number;
  rank: number;
};

export type RevealedTreasure = {
  id: string;
  category: string;
  rewardPoints: number;
  foundAt: string | null;
  finderDisplayName: string | null;
  poi: TreasurePlace;
};

export type TreasurePlace = {
  name: string;
  category: string;
  country: string;
  city: string;
  lat: number;
  lng: number;
  description: string | null;
  imageUrl: string | null;
  sourceUrl: string | null;
  sourceRef?: string | null;
  imageAttribution: string | null;
};

export type TreasureCard = {
  id: string;
  roundId: string;
  treasureId: string;
  rewardPoints: number;
  treasureCategory: string;
  place: TreasurePlace;
  foundAt: string;
};

export type PlayerProfile = {
  playerId: string;
  displayName: string;
  isOwnProfile: boolean;
  joinedAt: string;
  gamesCount: number;
  treasuresFound: number;
  totalRewardPoints: number;
  cards: TreasureCard[];
};

export type RoundState =
  | {
      state: 'active';
      round: PublicRound;
      lastClue: PublicClue | null;
      feed: PublicFeedEvent[];
      leaderboard: PublicLeaderboardRow[];
    }
  | {
      state: 'intermission';
      previousRound: PublicRound | null;
      nextStartsAt: string | null;
      revealedTreasures: RevealedTreasure[];
      feed: PublicFeedEvent[];
      leaderboard: PublicLeaderboardRow[];
    }
  | { state: 'empty'; message: string };

export type SessionPayload = {
  sessionId: string;
  player: Player;
  roundState: RoundState;
};

export type AppConfig = {
  publicBaseUrl: string;
  tapPricePoints: number;
  welcomeBonusPoints: number;
  map: {
    provider: string;
    styleUrl: string;
  };
  excludedCountries: string[];
};

export type BrowserTelemetry = {
  timezone?: string;
  language?: string;
  languages?: string[];
  platform?: string;
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  hardwareConcurrency?: number;
  maxTouchPoints?: number;
};

export type TapResponse = {
  tap: {
    tapId: string;
    pointsSpent: number;
    roundedDistanceMeters: number | null;
    directionDegrees: number | null;
    directionShown: boolean;
    directionUncertaintyDegrees: number;
    countryHit: boolean;
    countryName: string | null;
    cityHit: boolean;
    cityName: string | null;
    claimResult: 'none' | 'success';
    treasureFound?: {
      category: string;
      rewardPoints: number;
      place: TreasurePlace;
    };
  };
  player: Player;
  roundState: RoundState;
};

export class ApiError extends Error {
  constructor(public readonly code: string, message: string, public readonly status: number) {
    super(message);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {})
    },
    ...init
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(payload.error ?? 'request_failed', payload.message ?? 'Request failed', response.status);
  }
  return payload as T;
}

export const api = {
  config: () => request<AppConfig>('/api/config'),
  session: () => request<SessionPayload>('/api/session'),
  sessionTelemetry: (telemetry: BrowserTelemetry) => request<{ ok: boolean }>('/api/session/telemetry', {
    method: 'POST',
    body: JSON.stringify(telemetry)
  }),
  setDisplayName: (displayName: string) => request<{ player: Player }>('/api/player/display-name', {
    method: 'POST',
    body: JSON.stringify({ displayName })
  }),
  setEligibility: (ageConfirmed: boolean, country: string | null) => request<{ player: Player }>('/api/player/eligibility', {
    method: 'POST',
    body: JSON.stringify({ ageConfirmed, country })
  }),
  demoTopUp: (amount: number) => request<{ player: Player }>('/api/player/demo-top-up', {
    method: 'POST',
    body: JSON.stringify({ amount })
  }),
  treasureCards: () => request<{ cards: TreasureCard[] }>('/api/player/treasure-cards'),
  playerProfile: (playerId: string) => request<{ profile: PlayerProfile }>(`/api/players/${playerId}/profile`),
  currentRound: () => request<RoundState>('/api/round/current'),
  tap: (roundId: string, lat: number, lng: number, zoom: number) => request<TapResponse>(`/api/round/${roundId}/tap`, {
    method: 'POST',
    body: JSON.stringify({ lat, lng, zoom })
  }),
  adminOverview: () => request<any>('/api/admin/overview'),
  adminDashboard: () => request<any>('/api/admin/dashboard'),
  adminPlayers: (q = '') => request<any>(`/api/admin/players?q=${encodeURIComponent(q)}`),
  adminGeneratePois: () => request<any>('/api/admin/pois/generate', {
    method: 'POST',
    body: JSON.stringify({ targetCount: 20_000 })
  }),
  adminCreateRound: () => request<any>('/api/admin/rounds/create', {
    method: 'POST',
    body: JSON.stringify({})
  }),
  adminFinalizeRound: (roundId: string) => request<any>(`/api/admin/rounds/${roundId}/finalize`, {
    method: 'POST',
    body: JSON.stringify({})
  }),
  adminSetSetting: (settingKey: string, value: boolean) => request<any>('/api/admin/settings', {
    method: 'POST',
    body: JSON.stringify({ key: settingKey, value })
  }),
  adminGrant: (playerId: string, amount: number) => request<any>(`/api/admin/players/${playerId}/grant`, {
    method: 'POST',
    body: JSON.stringify({ amount, reason: 'manual_admin_grant' })
  }),
  adminBlock: (playerId: string, blocked: boolean) => request<any>(`/api/admin/players/${playerId}/block`, {
    method: 'POST',
    body: JSON.stringify({ blocked, reason: blocked ? 'manual_admin_block' : undefined })
  }),
  adminAnalytics: () => request<any>('/api/admin/analytics')
};
